import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import type { BlogPost } from '@/mocks/blog';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const navTabs = [
  { label: 'Home', href: '/' },
  { label: 'Marketplace', href: '/#marketplace' },
  { label: 'For Homes', href: '/#homes' },
  { label: 'For Businesses', href: '/#businesses' },
  { label: 'For Partners', href: '/#partners' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/#contact' },
];

export default function BlogAdminPage() {
  const navigate = useNavigate();
  const { posts, deletePost } = useBlogPosts();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');

  const handleDelete = useCallback((id: string) => {
    if (window.confirm('Are you sure you want to delete this blog post? This cannot be undone.')) {
      setDeletingId(id);
      setTimeout(() => {
        deletePost(id);
        setDeletingId(null);
      }, 300);
    }
  }, [deletePost]);

  const filteredPosts = posts.filter((p) => {
    if (filter === 'published') return p.status === 'published';
    if (filter === 'draft') return p.status === 'draft';
    return true;
  });

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Navigation Tabs */}
      <div className="sticky top-0 z-50 bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
                alt="AK Energy"
                className="h-7 w-auto"
              />
            </Link>
            <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide ml-4">
              {navTabs.map((tab) => (
                <Link
                  key={tab.label}
                  to={tab.href}
                  className="px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap"
                >
                  {tab.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-3"
            >
              <i className="ri-arrow-left-line" />
              Back to Blog
            </Link>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Blog Admin</h1>
            <p className="text-sm text-foreground-500 mt-1">Manage your blog posts, create new articles, and preview before publishing.</p>
          </div>
          <Link
            to="/blog/admin/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line" />
            New Post
          </Link>
        </div>

        {/* Stats + Filter */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2 bg-white border border-background-200 rounded-lg p-1">
            {(['all', 'published', 'draft'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer capitalize ${
                  filter === f
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                {f} ({f === 'all' ? posts.length : posts.filter((p) => p.status === f).length})
              </button>
            ))}
          </div>
          <span className="text-sm text-foreground-400 ml-auto">
            Total: {posts.length} posts
          </span>
        </div>

        {/* Posts Table */}
        <div className="bg-white border border-background-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-50 border-b border-background-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Post</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">Author</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filteredPosts.map((post) => (
                  <tr
                    key={post.id}
                    className={`hover:bg-background-50/50 transition-all ${deletingId === post.id ? 'opacity-30' : ''}`}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <img
                          src={post.coverImage}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover shrink-0 bg-background-100"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-950 truncate max-w-[200px] md:max-w-xs">{post.title}</p>
                          <p className="text-xs text-foreground-400 mt-0.5 truncate max-w-[200px] md:max-w-xs">{post.excerpt}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className="text-sm text-foreground-600">{post.author}</span>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <span className="text-xs text-foreground-500">{formatDate(post.publishedAt)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          post.status === 'published'
                            ? 'bg-green-50 text-green-700 border border-green-100'
                            : 'bg-yellow-50 text-yellow-700 border border-yellow-100'
                        }`}
                      >
                        {post.status === 'published' ? (
                          <><i className="ri-check-line mr-1" /> Published</>
                        ) : (
                          <><i className="ri-draft-line mr-1" /> Draft</>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/blog/${post.slug}`}
                          target="_blank"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                          title="View live post"
                        >
                          <i className="ri-eye-line" />
                        </Link>
                        <button
                          onClick={() => navigate(`/blog/admin/edit/${post.slug}`)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredPosts.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <i className="ri-article-line text-3xl text-foreground-300 mb-2 block" />
                      <p className="text-sm text-foreground-500">No posts found in this category.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}